import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { SIARFlowIndicator } from "@/components/admin/SIARFlowIndicator";
import { useUserContext } from "@/hooks/useUserContext";
import { 
  ClipboardList, 
  Plus, 
  Clock, 
  CheckCircle2, 
  AlertTriangle,
  User,
  Calendar,
  Filter,
  Loader2,
  Brain,
  TrendingUp,
  Target,
  Zap,
  Users,
  ChevronRight,
  RefreshCw,
  AlertCircle,
  Activity
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";

type SupervisorTask = {
  id: string;
  title: string;
  description: string | null;
  priority: string;
  status: string;
  source_type: string;
  source_id: string | null;
  due_date: string | null;
  created_at: string;
  assigned_to: string | null;
  verification_required: boolean | null;
  site_id: string | null;
  company_id: string | null;
};

type InterventionSuggestion = {
  id: string;
  intervention_type: string;
  target_type: string;
  target_worker_id: string | null;
  target_cluster_id: string | null;
  trigger_reason: string;
  suggested_intervention: {
    title?: string;
    message?: string;
    action_name?: string;
    expected_value?: number;
    reasoning?: string;
    delivery_method?: string;
  };
  ai_reasoning: string | null;
  status: string;
  priority: string | null;
  approval_tier: string;
  created_at: string;
  worker_profiles?: {
    display_name: string | null;
    current_risk_score: number | null;
    role: string | null;
  } | null;
};

interface ActionQueueProps {
  siteId?: string | null;
}

export default function ActionQueue({ siteId }: ActionQueueProps = {}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { companyId, isLoading: contextLoading } = useUserContext();
  const [taskFilter, setTaskFilter] = useState<string>("pending");
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<InterventionSuggestion | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    priority: "medium",
    source_type: "manual"
  });

  // Fetch supervisor tasks
  const { data: tasks, isLoading: tasksLoading } = useQuery({
    queryKey: ["supervisor-tasks", taskFilter, siteId, companyId],
    queryFn: async () => {
      if (!companyId) return [];
      
      let query = supabase
        .from("supervisor_tasks")
        .select("*")
        .eq("company_id", companyId)
        .order("created_at", { ascending: false });
      
      if (taskFilter !== "all") {
        query = query.eq("status", taskFilter);
      }

      if (siteId) {
        query = query.eq("site_id", siteId);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data as SupervisorTask[];
    },
    enabled: !contextLoading && !!companyId
  });

  // Fetch MDP intervention suggestions
  const { data: suggestions, isLoading: suggestionsLoading, refetch: refetchSuggestions } = useQuery({
    queryKey: ["intervention-suggestions", siteId, companyId],
    queryFn: async () => {
      if (!companyId) return [];
      
      let query = supabase
        .from("intervention_suggestions")
        .select(`
          *,
          worker_profiles:target_worker_id (
            display_name,
            current_risk_score,
            role
          )
        `)
        .eq("company_id", companyId)
        .order("created_at", { ascending: false })
        .limit(50);

      if (siteId) {
        // Get workers from this site
        const { data: workers } = await supabase
          .from("worker_profiles")
          .select("id")
          .eq("site_id", siteId);
        
        if (workers && workers.length > 0) {
          query = query.in("target_worker_id", workers.map(w => w.id));
        }
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data as InterventionSuggestion[];
    },
    enabled: !contextLoading && !!companyId
  });

  const createTaskMutation = useMutation({
    mutationFn: async (task: typeof newTask) => {
      // Get company_id from user profile
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        .from('worker_profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.company_id) {
        throw new Error('Could not determine company');
      }

      const { error } = await supabase
        .from("supervisor_tasks")
        .insert({
          company_id: profile.company_id,
          site_id: siteId || null,
          title: task.title,
          description: task.description,
          priority: task.priority,
          source_type: task.source_type,
          status: "pending"
        });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supervisor-tasks"] });
      setShowCreateTask(false);
      setNewTask({ title: "", description: "", priority: "medium", source_type: "manual" });
      toast.success("Task created successfully");
    },
    onError: () => toast.error("Failed to create task")
  });

  const updateTaskStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const updates: Record<string, unknown> = { status };
      if (status === "completed") {
        updates.completed_at = new Date().toISOString();
      }
      const { error } = await supabase
        .from("supervisor_tasks")
        .update(updates)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supervisor-tasks"] });
      toast.success("Task updated");
    }
  });

  const approveSuggestion = useMutation({
    mutationFn: async (suggestionId: string) => {
      const { error } = await supabase
        .from("intervention_suggestions")
        .update({ status: "approved" })
        .eq("id", suggestionId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["intervention-suggestions"] });
      setSelectedSuggestion(null);
      toast.success("Intervention approved and will be deployed");
    }
  });

  const rejectSuggestion = useMutation({
    mutationFn: async (suggestionId: string) => {
      const { error } = await supabase
        .from("intervention_suggestions")
        .update({ status: "rejected" })
        .eq("id", suggestionId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["intervention-suggestions"] });
      setSelectedSuggestion(null);
      toast.success("Intervention rejected");
    }
  });

  const generateRecommendations = async () => {
    setIsGenerating(true);
    try {
      const { error } = await supabase.functions.invoke("generate-intervention-recommendations", {
        body: { generateForAll: true }
      });
      if (error) throw error;
      await refetchSuggestions();
      toast.success("New recommendations generated");
    } catch (err) {
      console.error("Error generating recommendations:", err);
      toast.error("Failed to generate recommendations");
    } finally {
      setIsGenerating(false);
    }
  };

  const getPriorityColor = (priority: string | null) => {
    switch (priority) {
      case "critical": return "bg-destructive text-destructive-foreground";
      case "high": return "bg-orange-500 text-white";
      case "medium": return "bg-yellow-500 text-black";
      case "low": return "bg-muted text-muted-foreground";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="gap-1"><Clock className="h-3 w-3" />Pending Review</Badge>;
      case "approved":
        return <Badge className="bg-green-500 gap-1"><CheckCircle2 className="h-3 w-3" />Approved</Badge>;
      case "rejected":
        return <Badge variant="destructive" className="gap-1"><AlertCircle className="h-3 w-3" />Rejected</Badge>;
      case "deployed":
        return <Badge className="bg-blue-500 gap-1"><Zap className="h-3 w-3" />Deployed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getDeliveryMethodIcon = (method: string | undefined) => {
    switch (method) {
      case "popup": return <Zap className="h-4 w-4 text-yellow-500" />;
      case "quest": return <Target className="h-4 w-4 text-blue-500" />;
      case "supervisor_action": return <Users className="h-4 w-4 text-purple-500" />;
      default: return <Activity className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const taskStats = {
    pending: tasks?.filter(t => t.status === "pending").length || 0,
    inProgress: tasks?.filter(t => t.status === "in_progress").length || 0,
    completed: tasks?.filter(t => t.status === "completed").length || 0,
    mdpTasks: tasks?.filter(t => t.source_type === "mdp_recommendation").length || 0,
  };

  const suggestionStats = {
    pending: suggestions?.filter(s => s.status === "pending").length || 0,
    approved: suggestions?.filter(s => s.status === "approved").length || 0,
    supervisorActions: suggestions?.filter(s => s.suggested_intervention?.delivery_method === "supervisor_action").length || 0,
  };

  return (
    <div className="space-y-6">
      {/* SIAR Flow Indicator */}
      <SIARFlowIndicator 
        currentStep="actions" 
        context={{
          actionCount: taskStats.pending + suggestionStats.pending,
          resultCount: taskStats.completed
        }}
      />

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="h-6 w-6 text-primary" />
            MDP Action Queue
          </h2>
          <p className="text-muted-foreground">
            AI-powered intervention recommendations based on worker risk profiles
          </p>
        </div>
        <Button 
          onClick={generateRecommendations}
          disabled={isGenerating}
          className="gap-2"
        >
          {isGenerating ? (
            <><Loader2 className="h-4 w-4 animate-spin" />Generating...</>
          ) : (
            <><RefreshCw className="h-4 w-4" />Generate New Recommendations</>
          )}
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-orange-500/20 bg-orange-500/5">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/10">
                <Brain className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{suggestionStats.pending}</p>
                <p className="text-xs text-muted-foreground">Pending Review</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Users className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{suggestionStats.supervisorActions}</p>
                <p className="text-xs text-muted-foreground">Supervisor Actions</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <ClipboardList className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{taskStats.pending}</p>
                <p className="text-xs text-muted-foreground">Active Tasks</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-500/20 bg-green-500/5">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{suggestionStats.approved}</p>
                <p className="text-xs text-muted-foreground">Approved Today</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="recommendations" className="space-y-4">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="recommendations" className="gap-2">
            <Brain className="h-4 w-4" />
            MDP Recommendations
            {suggestionStats.pending > 0 && (
              <Badge variant="secondary" className="ml-1">{suggestionStats.pending}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="tasks" className="gap-2">
            <ClipboardList className="h-4 w-4" />
            Supervisor Tasks
            {taskStats.pending > 0 && (
              <Badge variant="secondary" className="ml-1">{taskStats.pending}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* MDP Recommendations Tab */}
        <TabsContent value="recommendations" className="space-y-4">
          {suggestionsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : suggestions?.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Brain className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg mb-2">No Recommendations Yet</h3>
                <p className="text-muted-foreground mb-4">
                  Click "Generate New Recommendations" to analyze at-risk workers and create intervention suggestions.
                </p>
                <Button onClick={generateRecommendations} disabled={isGenerating}>
                  {isGenerating ? "Generating..." : "Generate Recommendations"}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {suggestions?.filter(s => s.status === "pending").map((suggestion) => (
                <Card 
                  key={suggestion.id} 
                  className="hover:border-primary/50 transition-colors cursor-pointer"
                  onClick={() => setSelectedSuggestion(suggestion)}
                >
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        {getDeliveryMethodIcon(suggestion.suggested_intervention?.delivery_method)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-medium truncate">
                              {suggestion.suggested_intervention?.title || suggestion.intervention_type}
                            </h4>
                            <Badge className={getPriorityColor(suggestion.priority)}>
                              {suggestion.priority || "medium"}
                            </Badge>
                            {suggestion.suggested_intervention?.delivery_method === "supervisor_action" && (
                              <Badge variant="outline" className="text-purple-600 border-purple-300 gap-1">
                                <Users className="h-3 w-3" />
                                Supervisor Required
                              </Badge>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2 mt-1">
                            <User className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">
                              {suggestion.worker_profiles?.display_name || "Unknown Worker"}
                            </span>
                            {suggestion.worker_profiles?.current_risk_score && (
                              <Badge 
                                variant="outline" 
                                className={
                                  suggestion.worker_profiles.current_risk_score >= 70 
                                    ? "text-red-600 border-red-300" 
                                    : suggestion.worker_profiles.current_risk_score >= 40 
                                    ? "text-yellow-600 border-yellow-300"
                                    : "text-green-600 border-green-300"
                                }
                              >
                                Risk: {suggestion.worker_profiles.current_risk_score}%
                              </Badge>
                            )}
                          </div>

                          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                            {suggestion.suggested_intervention?.reasoning || suggestion.trigger_reason}
                          </p>

                          {suggestion.suggested_intervention?.expected_value && (
                            <div className="flex items-center gap-2 mt-2">
                              <TrendingUp className="h-3 w-3 text-green-500" />
                              <span className="text-xs text-muted-foreground">
                                Expected Value: <span className="font-medium text-green-600">
                                  +{suggestion.suggested_intervention.expected_value.toFixed(1)}
                                </span>
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(suggestion.created_at), { addSuffix: true })}
                        </span>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Show approved/rejected suggestions */}
              {suggestions?.filter(s => s.status !== "pending").length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">Recently Processed</h3>
                  <div className="space-y-2">
                    {suggestions?.filter(s => s.status !== "pending").slice(0, 5).map((suggestion) => (
                      <Card key={suggestion.id} className="bg-muted/30">
                        <CardContent className="py-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {getDeliveryMethodIcon(suggestion.suggested_intervention?.delivery_method)}
                              <div>
                                <span className="font-medium text-sm">
                                  {suggestion.suggested_intervention?.title || suggestion.intervention_type}
                                </span>
                                <span className="text-xs text-muted-foreground ml-2">
                                  → {suggestion.worker_profiles?.display_name}
                                </span>
                              </div>
                            </div>
                            {getStatusBadge(suggestion.status)}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* Supervisor Tasks Tab */}
        <TabsContent value="tasks" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={taskFilter} onValueChange={setTaskFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tasks</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Dialog open={showCreateTask} onOpenChange={setShowCreateTask}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="gap-2">
                  <Plus className="h-4 w-4" />
                  Manual Task
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Supervisor Task</DialogTitle>
                  <DialogDescription>
                    Create a manual task for supervisors to complete
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <Input
                    placeholder="Task title"
                    value={newTask.title}
                    onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  />
                  <Textarea
                    placeholder="Description"
                    value={newTask.description}
                    onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  />
                  <Select
                    value={newTask.priority}
                    onValueChange={(v) => setNewTask({ ...newTask, priority: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button 
                    className="w-full" 
                    onClick={() => createTaskMutation.mutate(newTask)}
                    disabled={!newTask.title || createTaskMutation.isPending}
                  >
                    {createTaskMutation.isPending ? "Creating..." : "Create Task"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {tasksLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : tasks?.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No tasks found. Tasks are automatically created from MDP supervisor action recommendations.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {tasks?.map((task) => (
                <Card 
                  key={task.id} 
                  className={`hover:bg-muted/50 transition-colors ${
                    task.source_type === "mdp_recommendation" ? "border-l-4 border-l-purple-500" : ""
                  }`}
                >
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        {task.status === "completed" ? (
                          <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                        ) : task.status === "in_progress" ? (
                          <Loader2 className="h-5 w-5 text-blue-500 animate-spin mt-0.5" />
                        ) : (
                          <Clock className="h-5 w-5 text-yellow-500 mt-0.5" />
                        )}
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-medium">{task.title}</h4>
                            <Badge className={getPriorityColor(task.priority)}>
                              {task.priority}
                            </Badge>
                            {task.source_type === "mdp_recommendation" && (
                              <Badge variant="outline" className="text-purple-600 border-purple-300 gap-1">
                                <Brain className="h-3 w-3" />
                                MDP
                              </Badge>
                            )}
                            {task.verification_required && (
                              <Badge variant="outline" className="text-xs">
                                Verification Required
                              </Badge>
                            )}
                          </div>
                          {task.description && (
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{task.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {format(new Date(task.created_at), "MMM d, yyyy")}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {task.status === "pending" && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => updateTaskStatus.mutate({ id: task.id, status: "in_progress" })}
                          >
                            Start
                          </Button>
                        )}
                        {task.status === "in_progress" && (
                          <Button 
                            size="sm"
                            onClick={() => updateTaskStatus.mutate({ id: task.id, status: "completed" })}
                          >
                            Complete
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Suggestion Detail Dialog */}
      <Dialog open={!!selectedSuggestion} onOpenChange={() => setSelectedSuggestion(null)}>
        <DialogContent className="max-w-2xl">
          {selectedSuggestion && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {getDeliveryMethodIcon(selectedSuggestion.suggested_intervention?.delivery_method)}
                  {selectedSuggestion.suggested_intervention?.title || selectedSuggestion.intervention_type}
                </DialogTitle>
                <DialogDescription>
                  Review and approve this AI-generated intervention recommendation
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Worker Info */}
                <Card>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{selectedSuggestion.worker_profiles?.display_name}</p>
                          <p className="text-sm text-muted-foreground capitalize">
                            {selectedSuggestion.worker_profiles?.role?.replace("_", " ")}
                          </p>
                        </div>
                      </div>
                      {selectedSuggestion.worker_profiles?.current_risk_score && (
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">Risk Score</p>
                          <div className="flex items-center gap-2">
                            <Progress 
                              value={selectedSuggestion.worker_profiles.current_risk_score} 
                              className="w-20 h-2"
                            />
                            <span className={`font-bold ${
                              selectedSuggestion.worker_profiles.current_risk_score >= 70 
                                ? "text-red-600" 
                                : selectedSuggestion.worker_profiles.current_risk_score >= 40 
                                ? "text-yellow-600"
                                : "text-green-600"
                            }`}>
                              {selectedSuggestion.worker_profiles.current_risk_score}%
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Intervention Details */}
                <div className="space-y-3">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Message</h4>
                    <p className="text-sm">{selectedSuggestion.suggested_intervention?.message}</p>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Trigger Reason</h4>
                    <p className="text-sm">{selectedSuggestion.trigger_reason}</p>
                  </div>

                  {selectedSuggestion.ai_reasoning && (
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">MDP Analysis</h4>
                      <p className="text-sm bg-muted p-3 rounded-lg">{selectedSuggestion.ai_reasoning}</p>
                    </div>
                  )}

                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <span className="text-muted-foreground">Delivery:</span>
                      <Badge variant="outline" className="capitalize">
                        {selectedSuggestion.suggested_intervention?.delivery_method?.replace("_", " ")}
                      </Badge>
                    </div>
                    {selectedSuggestion.suggested_intervention?.expected_value && (
                      <div className="flex items-center gap-1">
                        <span className="text-muted-foreground">Expected Value:</span>
                        <span className="font-medium text-green-600">
                          +{selectedSuggestion.suggested_intervention.expected_value.toFixed(1)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => rejectSuggestion.mutate(selectedSuggestion.id)}
                    disabled={rejectSuggestion.isPending}
                  >
                    Reject
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={() => approveSuggestion.mutate(selectedSuggestion.id)}
                    disabled={approveSuggestion.isPending}
                  >
                    {approveSuggestion.isPending ? "Approving..." : "Approve & Deploy"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}